import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchAudienceOverview,
  fetchAllContacts,
  fetchAudienceCount,
  previewAudience,
  fetchContactStats,
  fetchContactAnalytics,
  fetchSavedSegments,
  createSavedSegment,
  deleteSavedSegment,
  previewSavedSegment,
} from '../WhatsApp-Marketing/shared/whatsappApi.js';

// Server state for the WhatsApp Contacts page, the audience selector and the
// contact detail drawer. Contacts come from the shared CRM MySQL database.

const asMessage = (error, fallback) => error?.message || fallback;

export const loadAudienceOverview = createAsyncThunk(
  'whatsappContacts/loadAudienceOverview',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await fetchAudienceOverview();
      return data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load audience overview'));
    }
  }
);

export const loadContacts = createAsyncThunk(
  'whatsappContacts/loadContacts',
  async ({ limit = 50, offset = 0, company = null, search = '', filters = {} } = {}, { rejectWithValue }) => {
    try {
      const { data } = await fetchAllContacts({ limit, offset, company, search, filters });
      return data;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load contacts'));
    }
  }
);

export const loadAudienceCount = createAsyncThunk(
  'whatsappContacts/loadAudienceCount',
  async (audience, { rejectWithValue }) => {
    try {
      return await fetchAudienceCount(audience);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to count audience'));
    }
  }
);

export const previewAudienceContacts = createAsyncThunk(
  'whatsappContacts/previewAudience',
  async (audience, { rejectWithValue }) => {
    try {
      return await previewAudience(audience);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to preview audience'));
    }
  }
);

export const loadContactStats = createAsyncThunk(
  'whatsappContacts/loadContactStats',
  async (phone, { rejectWithValue }) => {
    try {
      return await fetchContactStats(phone);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load contact stats'));
    }
  }
);

export const loadContactAnalytics = createAsyncThunk(
  'whatsappContacts/loadContactAnalytics',
  async ({ phone, eventType, opts }, { rejectWithValue }) => {
    try {
      return await fetchContactAnalytics(phone, eventType, opts);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load contact analytics'));
    }
  }
);

export const loadSavedSegments = createAsyncThunk(
  'whatsappContacts/loadSavedSegments',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchSavedSegments();
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to load saved segments'));
    }
  }
);

export const addSavedSegment = createAsyncThunk(
  'whatsappContacts/addSavedSegment',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      const created = await createSavedSegment(data);
      dispatch(loadSavedSegments());
      return created;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to create segment'));
    }
  }
);

export const removeSavedSegment = createAsyncThunk(
  'whatsappContacts/removeSavedSegment',
  async (id, { rejectWithValue }) => {
    try {
      await deleteSavedSegment(id);
      return id;
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to delete segment'));
    }
  }
);

export const previewSegment = createAsyncThunk(
  'whatsappContacts/previewSegment',
  async (data, { rejectWithValue }) => {
    try {
      return await previewSavedSegment(data);
    } catch (error) {
      return rejectWithValue(asMessage(error, 'Failed to preview segment'));
    }
  }
);

const initialState = {
  overview: null,
  contacts: [],
  totalCount: 0,
  loading: true,
  contactStats: null,
  contactStatsLoading: false,
  savedSegments: [],
  segmentsLoading: false,
  error: null,
};

const whatsappContactsSlice = createSlice({
  name: 'whatsappContacts',
  initialState,
  reducers: {
    clearContactStats: (state) => {
      state.contactStats = null;
    },
    clearContactsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAudienceOverview.fulfilled, (state, action) => {
        state.overview = action.payload;
      })

      .addCase(loadContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadContacts.fulfilled, (state, action) => {
        state.loading = false;
        state.contacts = action.payload?.contacts || [];
        state.totalCount = action.payload?.totalCount ?? 0;
      })
      .addCase(loadContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(loadContactStats.pending, (state) => {
        state.contactStatsLoading = true;
      })
      .addCase(loadContactStats.fulfilled, (state, action) => {
        state.contactStatsLoading = false;
        state.contactStats = action.payload;
      })
      .addCase(loadContactStats.rejected, (state, action) => {
        state.contactStatsLoading = false;
        state.error = action.payload;
      })

      .addCase(loadSavedSegments.pending, (state) => {
        state.segmentsLoading = true;
      })
      .addCase(loadSavedSegments.fulfilled, (state, action) => {
        state.segmentsLoading = false;
        state.savedSegments = action.payload;
      })
      .addCase(loadSavedSegments.rejected, (state, action) => {
        state.segmentsLoading = false;
        state.error = action.payload;
      })
      .addCase(removeSavedSegment.fulfilled, (state, action) => {
        state.savedSegments = state.savedSegments.filter(
          (s) => s.id !== action.payload && s._id !== action.payload
        );
      });
  },
});

export const { clearContactStats, clearContactsError } = whatsappContactsSlice.actions;

export default whatsappContactsSlice.reducer;
